// admin/AdminShell.tsx — the chrome for an authenticated, connected admin.
//
// Reads from Redux (dashboard.admin for the topbar identity, connection for
// the banner). Sidebar nav + content area; route held in local state since
// it's transient UI not data.
//
// Layout behaviour:
//   - Wide viewports (≥ 768px) — sidebar and content are sibling flex
//     columns inside a viewport-height shell. EACH gets its own scroll;
//     a long table in the content doesn't drag the sidebar.
//   - Narrow viewports (< 768px) — sidebar collapses behind a hamburger
//     in the topbar. Tap to slide it out as an overlay (240px wide,
//     dark backdrop). Tapping a nav item or the backdrop dismisses it.

import { useEffect, useState, type CSSProperties, type RefObject } from "react";
import type { Socket } from "socket.io-client";
import { styles, tokens } from "./styles";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import Ideas from "./pages/Ideas";
import Sfx from "./pages/Sfx";
import Referrals from "./pages/Referrals";
import AccountDeletions from "./pages/AccountDeletions";
import { logout } from "../services/authApi";
import { useAppSelector } from "./store";

type Route =
  | "dashboard"
  | "users"
  | "ideas"
  | "sfx"
  | "referrals"
  | "deletions";

interface AdminShellProps {
  socket: RefObject<Socket | null>;
  onSignOut: () => void;
}

const NAV: Array<{ id: Route; label: string }> = [
  { id: "dashboard", label: "Dashboard" },
  { id: "users", label: "Users" },
  { id: "ideas", label: "Ideas" },
  { id: "referrals", label: "Referrals" },
  { id: "deletions", label: "Deletions" },
  { id: "sfx", label: "SFX" },
];

const NARROW_MQ = "(max-width: 767px)";

export default function AdminShell({ socket, onSignOut }: AdminShellProps) {
  const [route, setRoute] = useState<Route>("dashboard");
  const [isNarrow, setIsNarrow] = useState<boolean>(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(NARROW_MQ).matches
      : false,
  );
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const admin = useAppSelector((s) => s.dashboard.admin);
  const serverVersion = useAppSelector((s) => s.dashboard.serverVersion);
  const connection = useAppSelector((s) => s.connection);

  // Watch the viewport — flip layout mode when it crosses 768px. Closing
  // the overlay on widen prevents a stale "drawer open" state from showing
  // up if the user resizes / rotates while it's expanded.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(NARROW_MQ);
    const onChange = () => {
      setIsNarrow(mql.matches);
      if (!mql.matches) setSidebarOpen(false);
    };
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  // Escape closes the drawer.
  useEffect(() => {
    if (!sidebarOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSidebarOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sidebarOpen]);

  async function handleSignOut() {
    try {
      await logout();
    } finally {
      onSignOut();
    }
  }

  function handleNavClick(id: Route) {
    setRoute(id);
    if (isNarrow) setSidebarOpen(false);
  }

  const navList = (
    <>
      {NAV.map((item) => {
        const isActive = item.id === route;
        return (
          <button
            key={item.id}
            type="button"
            style={{
              ...styles.navItem,
              ...(isActive ? styles.navItemActive : {}),
            }}
            onClick={() => handleNavClick(item.id)}
          >
            {item.label}
          </button>
        );
      })}
    </>
  );

  return (
    <div style={styles.shell} className="ember-admin-shell">
      {/* Admin portal global CSS. Two concerns:
          1. Suppress the browser's default mouse-click focus ring on
             buttons (keeps :focus-visible for keyboard a11y).
          2. Bump text up on touch devices — the desktop sizing (shell
             base 14px, pills/cells 11-13px) reads uncomfortably small
             on iPad/iPhone. pointer:coarse gates the bumps to touch
             only so desktop is unchanged. */}
      <style>{`
        .ember-admin-shell button:focus {
          outline: none;
        }
        .ember-admin-shell button:focus-visible {
          outline: 2px solid ${tokens.accent};
          outline-offset: -2px;
        }
        @media (pointer: coarse) {
          /* Shell base — most descendants inherit from here. */
          .ember-admin-shell {
            font-size: 16px;
          }
          /* Nav items, action buttons, kebab triggers, sign-out. */
          .ember-admin-shell button {
            font-size: 15px;
          }
          /* Table cells, pills, sort headers, and any small chip-style
             text get bumped via wildcard descendant rules — !important
             wins against inline fontSize values for elements explicitly
             set below 14px. Only affects the admin shell. */
          .ember-admin-shell td,
          .ember-admin-shell th {
            font-size: 14px !important;
          }
        }
      `}</style>
      {connection.state === "fatal" ? (
        <FatalOverlay error={connection.error} />
      ) : null}

      <header style={styles.topbar}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {isNarrow && (
            <button
              type="button"
              style={hamburgerStyle}
              onClick={() => setSidebarOpen((s) => !s)}
              aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={sidebarOpen}
            >
              ☰
            </button>
          )}
          <div style={styles.brand}>
            <h1 style={styles.brandTitle}>Ember Hollow</h1>
            <span style={styles.brandSub}>Admin</span>
          </div>
        </div>
        <div style={styles.topbarRight}>
          <span style={styles.versionTag}>
            Server v{serverVersion}, Client v{__APP_VERSION__}
          </span>
          <span>{admin?.email ?? "—"}</span>
          <button
            type="button"
            style={styles.signoutBtn}
            onClick={handleSignOut}
          >
            Sign out
          </button>
        </div>
      </header>

      {connection.state === "connecting" ||
      connection.state === "reconnecting" ? (
        <ConnectionBanner state={connection.state} />
      ) : null}

      <div style={styles.shellRow}>
        {/* Wide: sidebar is inline; narrow: rendered only when drawer open
            as a fixed overlay on top of the content with a backdrop. */}
        {!isNarrow ? (
          <nav style={styles.sidebar}>{navList}</nav>
        ) : sidebarOpen ? (
          <>
            <div
              style={drawerBackdropStyle}
              onClick={() => setSidebarOpen(false)}
              role="presentation"
            />
            <nav
              style={drawerSidebarStyle}
              role="dialog"
              aria-label="Navigation"
            >
              {navList}
            </nav>
          </>
        ) : null}

        <main style={styles.content}>
          {route === "dashboard" ? (
            <Dashboard />
          ) : route === "users" ? (
            <Users socket={socket} viewerUserId={admin?.id ?? ""} />
          ) : route === "ideas" ? (
            <Ideas socket={socket} />
          ) : route === "referrals" ? (
            <Referrals socket={socket} />
          ) : route === "deletions" ? (
            <AccountDeletions socket={socket} />
          ) : (
            <Sfx socket={socket} />
          )}
        </main>
      </div>
    </div>
  );
}

// Hamburger button — only rendered when isNarrow. Sits left-of-brand in
// the topbar. Square, monochrome, no special hover treatment so it reads
// as utilitarian.
const hamburgerStyle: CSSProperties = {
  background: "transparent",
  color: tokens.inkPrimary,
  border: `1px solid ${tokens.border}`,
  borderRadius: 4,
  fontSize: 18,
  cursor: "pointer",
  padding: "4px 10px",
  fontFamily: tokens.fontBody,
  lineHeight: 1,
};

// Drawer overlay backdrop — semi-opaque, sits below the drawer pane but
// above all content. Tapping it dismisses the drawer.
const drawerBackdropStyle: CSSProperties = {
  position: "fixed",
  top: 56, // start below the topbar so the brand + hamburger remain accessible
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(8, 10, 12, 0.55)",
  zIndex: 50,
};

// Drawer sidebar pane — a fixed-position 240px column sliding in from the
// left below the topbar. Reuses the same nav button styles via navList.
const drawerSidebarStyle: CSSProperties = {
  ...styles.sidebar,
  position: "fixed",
  top: 56,
  left: 0,
  bottom: 0,
  width: 240,
  flex: "0 0 240px",
  zIndex: 51,
  boxShadow: "4px 0 12px rgba(0,0,0,0.45)",
};

function ConnectionBanner({ state }: { state: "connecting" | "reconnecting" }) {
  return (
    <div
      style={{
        background: tokens.warnMuted,
        color: tokens.warn,
        borderBottom: `1px solid ${tokens.border}`,
        padding: "6px 24px",
        fontSize: 12,
        letterSpacing: "0.04em",
        textAlign: "center",
      }}
      role="status"
      aria-live="polite"
    >
      {state === "connecting"
        ? "Connecting to admin server…"
        : "Connection lost. Reconnecting…"}
    </div>
  );
}

function FatalOverlay({ error }: { error: string }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8, 10, 12, 0.85)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      role="alertdialog"
      aria-modal="true"
    >
      <div
        style={{
          background: tokens.panel,
          border: `1px solid ${tokens.borderStrong}`,
          borderRadius: 8,
          padding: 24,
          maxWidth: 400,
          textAlign: "center",
        }}
      >
        <h2
          style={{
            margin: "0 0 12px",
            fontSize: 18,
            color: tokens.destructive,
          }}
        >
          Connection lost
        </h2>
        <p style={{ margin: "0 0 16px", color: tokens.inkMuted, fontSize: 13 }}>
          {error || "The admin connection was closed by the server."}
        </p>
        <button
          type="button"
          style={styles.signoutBtn}
          onClick={() => window.location.reload()}
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
